import React, { useMemo } from 'react';
import { Box, Divider } from '@mui/material';
import { KanbanContextProvider } from '@/context/kanbanContext';
import { KanbanContent } from './KanbanContent';
import { InstantSearch } from 'react-instantsearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { KanbanFiltersRow } from './KanbanFiltersRow';
import { DEFAULT_HIDDEN_COLUMNS } from '@/context/columnControlContext.jsx';
import { TableSortContextProvider } from '@/context/tableSortContext';
import { usePreviewMode } from '@/hooks/usePreviewMode';
import { useSelector } from 'react-redux';
import { PoliciesSelectProvider } from '@/context/policiesContext';
import { CompleteDealDialog } from '@/components/dialogs/CompleteDealDialog';
import { ObjectContextProvider } from '@/context/ObjectContext';

const KanbanCompleteDealDialog = () => {
	const completeDealOpen = useSelector((state) => state.dialog.open?.completeDeal);
	const dealId = useSelector((state) => state.dialog.data?.completeDeal?.dealId);

	if (!completeDealOpen || !dealId) {
		return null;
	}

	return (
		<ObjectContextProvider dealIdOverride={String(dealId)}>
			<PoliciesSelectProvider>
				<CompleteDealDialog />
			</PoliciesSelectProvider>
		</ObjectContextProvider>
	);
};

export const KanbanView = ({ pipelineId, objectType = 'deal', children }) => {
	const { isPreviewMode } = usePreviewMode();
	const isProspect = objectType === 'prospect';

	const dialogs = useSelector((state) => state.dialog?.open);
	const dialogsOpen = useMemo(() => {
		const otherDialogs = Object.entries(dialogs).filter(([_dialogName, isOpen]) => isOpen);
		return otherDialogs.length > 0;
	}, [dialogs]);

	const indexName = isProspect ? 'clients' : 'deals';
	const searchClient = isProspect ? clientSearch : dealSearch;

	return (
		<>
			<Box
				sx={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					...((isPreviewMode || dialogsOpen) && {
						pointerEvents: 'none',
						userSelect: 'none',
					}),
				}}
			>
				<InstantSearch indexName={indexName} searchClient={searchClient}>
					<KanbanContextProvider
						pipelineId={pipelineId}
						objectType={objectType}
						defaultHiddenColumns={isProspect ? [] : DEFAULT_HIDDEN_COLUMNS}
					>
						<TableSortContextProvider value={pipelineId} variant='kanban'>
							<KanbanFiltersRow />
							<Divider />
							<KanbanContent />
							{children}
						</TableSortContextProvider>
					</KanbanContextProvider>
				</InstantSearch>
			</Box>
			<KanbanCompleteDealDialog />
		</>
	);
};
